import { TLBHScore } from '@/types'
import { ScoreDisplay } from '@/components/ui/ScoreDisplay'
import { ScoreGauge } from '@/components/ui/ScoreGauge'
import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle } from 'lucide-react'
import { cn } from '@/utils/cn'

interface ScoreOverviewProps {
  score: TLBHScore
  previousScore?: number
  showTrend?: boolean
  layout?: 'horizontal' | 'vertical'
  className?: string
}

export function ScoreOverview({
  score,
  previousScore,
  showTrend = true,
  layout = 'horizontal',
  className
}: ScoreOverviewProps) {
  const scoreDiff = previousScore ? score.tlbhScore - previousScore : 0
  const trendDirection = scoreDiff > 0 ? 'up' : scoreDiff < 0 ? 'down' : 'stable'
  
  const TrendIcon = {
    up: TrendingUp,
    down: TrendingDown,
    stable: Minus
  }[trendDirection]

  const trendColor = {
    up: 'text-green-600',
    down: 'text-red-600',
    stable: 'text-gray-500'
  }[trendDirection]

  const isLayoutVertical = layout === 'vertical'

  return (
    <div className={cn(
      'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6',
      className
    )}>
      <div className={cn(
        'flex gap-6',
        isLayoutVertical ? 'flex-col items-center' : 'items-center'
      )}>
        {/* Score Gauge */}
        <div className="flex-shrink-0">
          <ScoreGauge
            score={score.tlbhScore}
            band={score.band}
            size={140}
            thickness={12}
            animated={true}
          />
        </div>

        {/* Score Details */}
        <div className={cn(
          'flex-1',
          isLayoutVertical ? 'text-center' : ''
        )}>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              TLBH Score
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Last updated: {new Date(score.calculatedAt).toLocaleDateString()}
            </p>
          </div>

          {/* Score Trend */}
          {showTrend && previousScore && (
            <div className="flex items-center gap-2 mb-4">
              <TrendIcon className={cn('w-4 h-4', trendColor)} />
              <span className={cn('text-sm font-medium', trendColor)}>
                {scoreDiff > 0 ? '+' : ''}{scoreDiff.toFixed(1)} points
              </span>
              <span className="text-gray-500 text-sm">
                from last calculation
              </span>
            </div>
          )}

          {/* Confidence Score */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Confidence:
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(score.confidence, 100)}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {score.confidence.toFixed(0)}%
              </span>
            </div>
          </div>

          {/* Red Flags Alert */}
          {score.redFlags && score.redFlags.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-700 dark:text-red-300">
                {score.redFlags.length} active red flag{score.redFlags.length > 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Data Sources */}
          <div className="mt-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Based on {score.dataSourcesUsed?.length || 0} data sources
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}